/**
 * Recognising a 3MF, which is the most a pure reader can do with one.
 *
 * A 3MF is a zip holding XML, so measuring one means a decompressor and an XML parser —
 * which is the reason this format was passed over for a long time, and the reason nothing
 * here reads its contents. Reading is left to three's `3MFLoader`, reached through
 * `readModelFile` for exactly the reason G-code is: a layer defined by having no renderer
 * in it cannot be where the renderer's loaders are called.
 *
 * Recognising one needs neither, because a zip writes the name of each entry in plain text
 * immediately before that entry's data, whether or not the data itself is compressed. So
 * the names can be read straight out of the bytes.
 */

/** `PK\x03\x04`: the local file header every zip entry begins with, and so every zip. */
const ZIP_SIGNATURE = [0x50, 0x4b, 0x03, 0x04];

/**
 * How far in to look for the model entry.
 *
 * The entries a 3MF opens with — the content types and the relationships — are a few
 * hundred bytes between them, so the model's own header falls well inside this. The
 * model's *data* may be enormous, but its name is written before it, which is all this
 * needs to see.
 */
const SCAN_BYTES = 64 * 1024;

/**
 * Does this look like a 3MF?
 *
 * Both halves are needed. A zip alone could be anything, and the other package formats
 * built on the same conventions — a .docx, say — would pass a test that only asked
 * whether the file were a zip and whether it carried a content-types part.
 */
export function looksLike3mf(buffer: ArrayBuffer): boolean {
  if (!startsWithZipSignature(buffer)) return false;

  const head = new Uint8Array(buffer, 0, Math.min(buffer.byteLength, SCAN_BYTES));
  // Latin-1 rather than UTF-8: entry names are bytes, and decoding them as UTF-8 would
  // fail on a package that happens to carry a name this viewer cannot spell. Every byte
  // maps to a character here, so the search is over the bytes themselves.
  const names = new TextDecoder('latin1').decode(head);

  return names.includes('.model');
}

function startsWithZipSignature(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < ZIP_SIGNATURE.length) return false;

  const opening = new Uint8Array(buffer, 0, ZIP_SIGNATURE.length);
  return ZIP_SIGNATURE.every((byte, index) => opening[index] === byte);
}

/**
 * What sort of property group a resource id names.
 *
 * Only the three the renderer's loader can draw from. Anything else is a group we cannot
 * redirect to and so is not worth naming.
 */
export type PropertyGroup = 'texture' | 'colour' | 'material';

/** A `<multiproperties>` resource: several property groups, indexed together. */
export interface MultiProperties {
  /** The groups it combines, in the order its rows index them. */
  readonly pids: readonly string[];
  /** One row per `<multi>`, holding one index into each of `pids`. */
  readonly rows: readonly (readonly number[])[];
}

/** A group to draw from instead, and how to reach it from a multi-property index. */
export interface ChosenLayer {
  readonly pid: string;
  /** The index into `pid` that a given index into the multi-property group stands for. */
  indexFor(index: number): number;
}

/**
 * Which single layer of a multi-property group to draw.
 *
 * A `<multiproperties>` stacks several property groups on the same triangles — a colour
 * under a texture under another texture — and three's loader draws none of them: the
 * resource type is one it does not recognise, so every triangle referring to it is
 * dropped and the model comes out with a hole where they were.
 *
 * Redirecting the triangles at one of the layers is what makes them drawable again. A
 * texture is preferred over a colour, and a colour over a base material, because the more
 * a layer says the more is lost by discarding it: a flat colour under a picture can be
 * guessed at from the picture, and the reverse is not true.
 *
 * The layers below the chosen one are not composited in. Doing that properly means
 * blending the groups' images by the methods the file names, at which point we would be
 * reimplementing most of the materials extension rather than getting a face to appear.
 */
export function chooseLayer(
  properties: MultiProperties,
  groupOf: (pid: string) => PropertyGroup | undefined,
): ChosenLayer | undefined {
  const ranked: readonly PropertyGroup[] = ['texture', 'colour', 'material'];

  let chosen: { pid: string; at: number } | undefined;
  let bestRank = ranked.length;

  properties.pids.forEach((pid, at) => {
    const group = groupOf(pid);
    if (group === undefined) return;

    const rank = ranked.indexOf(group);
    // Strictly better, so the first of two equally good layers wins — the file lists them
    // bottom upwards, and the lowest is the one the others were meant to sit on.
    if (rank < bestRank) {
      bestRank = rank;
      chosen = { pid, at };
    }
  });

  if (!chosen) return undefined;
  const { pid, at } = chosen;

  return {
    pid,
    indexFor: (index) => properties.rows[index]?.[at] ?? 0,
  };
}

/**
 * Millimetres per unit, for each unit a 3MF may declare.
 *
 * The format states its own unit on the model element and defaults to millimetres. three's
 * loader reads the attribute and then never applies it, so a file in centimetres arrives
 * ten times too small and one in inches twenty-five times too large, with nothing to say
 * so — the model simply looks the wrong size.
 */
const MILLIMETRES: ReadonlyMap<string, number> = new Map([
  ['micron', 0.001],
  ['millimeter', 1],
  ['centimeter', 10],
  ['inch', 25.4],
  ['foot', 304.8],
  ['meter', 1000],
]);

/**
 * How many millimetres one of this file's units is.
 *
 * An unfamiliar or absent unit is millimetres, which is both the format's own default and
 * the assumption everything else here makes. Guessing anything else would silently resize
 * a model over a spelling we did not recognise.
 */
export function millimetresPerUnit(declared: string | null | undefined): number {
  if (declared === null || declared === undefined) return 1;
  return MILLIMETRES.get(declared.trim().toLowerCase()) ?? 1;
}

/**
 * Whether a 3MF colour string says the surface is see-through.
 *
 * The format writes a colour as six hexadecimal digits, or eight where the last pair is an
 * alpha. three's loader reads that alpha off a base material and then never marks the
 * material transparent, so the number it worked out is ignored; off a colour group it
 * discards the alpha entirely. Either way a file that says it is translucent is drawn
 * solid, and this is what notices.
 */
export function opacityOf(colour: string | null | undefined): number {
  if (colour === null || colour === undefined) return 1;

  const value = colour.trim();
  if (!/^#[0-9a-f]{8}$/i.test(value)) return 1;

  return Number.parseInt(value.slice(7, 9), 16) / 255;
}

/**
 * Does this image carry an alpha channel?
 *
 * Read from the header rather than by decoding: a PNG states its colour type in the first
 * chunk, and a JPEG has no alpha to state. Knowing costs a handful of bytes, and the answer
 * decides whether a textured surface has to be drawn as a translucent one — which nothing
 * else can tell us, since a decoded image looks the same either way until its pixels are
 * examined.
 */
export function imageHasAlpha(bytes: Uint8Array): boolean {
  // 8-byte signature, then a 4-byte length and the `IHDR` tag, then width, height, depth,
  // and at offset 25 the colour type. Types 4 and 6 are the two carrying alpha.
  const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47];
  if (bytes.length < 26) return false;
  if (!PNG_SIGNATURE.every((byte, index) => bytes[index] === byte)) return false;

  const colourType = bytes[25];
  return colourType === 4 || colourType === 6;
}
